import { useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import type { AssistantPageKey } from '../constants/assistantSuggestedQueries';
import {
  buildAssistantRouteContext,
  buildVoiceSessionOrigin,
  formatSessionOriginLabel,
} from '../utils/assistantPageContext';
import {
  getCurrentSessionId,
  getOrCreateCurrentSession,
  type VoiceSessionOrigin,
} from '../utils/voiceSessionStorage';

export interface DashboardAssistantMetrics {
  cashPosition?: number;
  receivables?: number;
  payables?: number;
  pendingReviews?: number;
  currency?: string;
}

export interface AssistantPageContext extends DashboardAssistantMetrics {
  pageName: string;
  pageKey: AssistantPageKey;
  pathname: string;
  search: string;
  initiatedFrom?: VoiceSessionOrigin & { pageName?: string };
  suggestedQuotes?: QuotesAssistantContext['suggestedQuotes'];
  selectedQuoteIds?: number[];
}

export interface QuotesAssistantContext {
  suggestedQuotes?: Array<{
    id: number;
    quote_number: string;
    customer_name?: string;
    name: string;
    index: number;
  }>;
  selectedQuoteIds?: number[];
}

const CONTEXT_EVENT = 'dashboardAssistantContextUpdate';
const QUOTES_CONTEXT_EVENT = 'quotesAssistantContextUpdate';

export function publishQuotesAssistantContext(detail: QuotesAssistantContext): void {
  window.dispatchEvent(new CustomEvent(QUOTES_CONTEXT_EVENT, { detail }));
}

/** Dashboard publishes metric snapshots; Layout merges them into voice assistant context. */
export function publishDashboardAssistantContext(detail: DashboardAssistantMetrics): void {
  window.dispatchEvent(new CustomEvent(CONTEXT_EVENT, { detail }));
}

export function notifyVoiceSessionChanged(): void {
  window.dispatchEvent(new CustomEvent('voiceSessionChanged'));
}

export function useAssistantPageContext(
  pathname: string,
  search: string,
  pageName: string,
  isDashboard: boolean,
  t: TFunction,
  business?: { base_currency_code?: string } | null
): AssistantPageContext {
  const [metrics, setMetrics] = useState<DashboardAssistantMetrics>({});
  const [quotesContext, setQuotesContext] = useState<QuotesAssistantContext>({});
  const [sessionOrigin, setSessionOrigin] = useState<VoiceSessionOrigin | undefined>();

  const route = useMemo(() => buildAssistantRouteContext(pathname, search), [pathname, search]);

  useEffect(() => {
    const origin = buildVoiceSessionOrigin(pathname, search);
    const session = getOrCreateCurrentSession(origin);
    setSessionOrigin(session.origin ?? origin);
  }, [pathname, search]);

  useEffect(() => {
    if (!isDashboard) return undefined;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<DashboardAssistantMetrics>).detail;
      if (detail) setMetrics(detail);
    };

    window.addEventListener(CONTEXT_EVENT, handler as EventListener);
    return () => window.removeEventListener(CONTEXT_EVENT, handler as EventListener);
  }, [isDashboard]);

  useEffect(() => {
    if (route.pageKey !== 'quotes') {
      setQuotesContext({});
      return undefined;
    }

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<QuotesAssistantContext>).detail;
      if (detail) setQuotesContext(detail);
    };

    window.addEventListener(QUOTES_CONTEXT_EVENT, handler as EventListener);
    return () => window.removeEventListener(QUOTES_CONTEXT_EVENT, handler as EventListener);
  }, [route.pageKey]);

  useEffect(() => {
    const onSessionChange = () => {
      const currentId = getCurrentSessionId();
      if (!currentId) {
        setSessionOrigin(undefined);
        return;
      }
      const session = getOrCreateCurrentSession(buildVoiceSessionOrigin(pathname, search));
      setSessionOrigin(session.origin);
    };

    window.addEventListener('voiceSessionChanged', onSessionChange as EventListener);
    return () => window.removeEventListener('voiceSessionChanged', onSessionChange as EventListener);
  }, [pathname, search]);

  return useMemo(() => {
    const initiatedPageName = formatSessionOriginLabel(sessionOrigin, (key) => t(key));

    return {
      pageName,
      pageKey: route.pageKey,
      pathname,
      search,
      initiatedFrom: sessionOrigin
        ? {
            ...sessionOrigin,
            pageName: initiatedPageName,
          }
        : undefined,
      suggestedQuotes: quotesContext.suggestedQuotes,
      selectedQuoteIds: quotesContext.selectedQuoteIds,
      ...(isDashboard
        ? {
            currency: metrics.currency ?? business?.base_currency_code,
            cashPosition: metrics.cashPosition,
            receivables: metrics.receivables,
            payables: metrics.payables,
            pendingReviews: metrics.pendingReviews,
          }
        : {}),
    };
  }, [
    business?.base_currency_code,
    isDashboard,
    metrics,
    pageName,
    pathname,
    route.pageKey,
    search,
    sessionOrigin,
    t,
    quotesContext.selectedQuoteIds,
    quotesContext.suggestedQuotes,
  ]);
}

/** @deprecated Use useAssistantPageContext */
export const useDashboardAssistantContext = useAssistantPageContext;
